import { useEffect, useMemo, useRef, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Stack,
} from "@mui/material";
import { ArrowUpward, ArrowDownward, Today } from "@mui/icons-material";
import TimelineCard from "./TimelineCard";
import InitiativeDialog from "./InitiativeDialog";
import useInitiativeSelection from "../hooks/useInitiativeSelection";
import { useLookups } from "../hooks/useLookups";
import { useInitiativeSort } from "../hooks/useInitiativeSort";
import { getValueComparators } from "../utils/valueComparators";
import {
  parseTargetPeriod,
  formatQuarterIndex,
  getCurrentQuarterIndex,
} from "../utils/targetPeriod";
import { SORT_OPTIONS } from "../constants/sortOptions";

export default function TimelineView({
  initiatives,
  mutations,
  onInitiativeChange,
}) {
  const theme = useTheme();
  const selection = useInitiativeSelection(mutations);
  const canDrag = Boolean(onInitiativeChange);
  const lookups = useLookups();
  const valueComparators = useMemo(
    () => getValueComparators(lookups),
    [lookups],
  );
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverQuarter, setDragOverQuarter] = useState(null);
  const [dragGrabOffset, setDragGrabOffset] = useState(0);
  const { sortColumn, setSortColumn, sortDir, setSortDir } =
    useInitiativeSort();
  const [optimisticMove, setOptimisticMove] = useState(null);

  const handleSortColumnChange = (value) => {
    setSortColumn(value === "default" ? null : value);
  };

  const handleToggleSortDir = () =>
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));

  const { scheduled, unscheduledCount } = useMemo(() => {
    const scheduled = [];
    let unscheduledCount = 0;
    initiatives.forEach((initiative) => {
      const targetPeriod =
        optimisticMove?.id === initiative.id
          ? optimisticMove.targetPeriod
          : initiative.targetPeriod;
      const startIndex = parseTargetPeriod(targetPeriod);
      if (startIndex > 0) {
        scheduled.push({ ...initiative, targetPeriod, startIndex });
      } else {
        unscheduledCount += 1;
      }
    });
    if (sortColumn) {
      const dir = sortDir === "asc" ? 1 : -1;
      const compare = valueComparators[sortColumn];
      if (compare) {
        scheduled.sort((a, b) => dir * compare(a[sortColumn], b[sortColumn]));
      }
    }
    return { scheduled, unscheduledCount };
  }, [initiatives, sortColumn, sortDir, valueComparators, optimisticMove]);

  const minIndex = scheduled.length
    ? Math.min(...scheduled.map((i) => i.startIndex)) - 2
    : 0;
  const maxIndex = scheduled.length
    ? Math.max(...scheduled.map((i) => i.startIndex + (i.estimate || 1) - 1)) +
      2
    : 0;
  const quarterCount = maxIndex - minIndex + 1;
  const quarters = Array.from({ length: quarterCount }, (_, i) => minIndex + i);

  const currentQuarterIndex = getCurrentQuarterIndex();
  const hasCurrentQuarterColumn =
    scheduled.length > 0 &&
    currentQuarterIndex >= minIndex &&
    currentQuarterIndex <= maxIndex;

  const scrollAreaRef = useRef(null);
  const currentQuarterCellRef = useRef(null);
  const [currentQuarterVisible, setCurrentQuarterVisible] = useState(true);

  useEffect(() => {
    if (
      !hasCurrentQuarterColumn ||
      !scrollAreaRef.current ||
      !currentQuarterCellRef.current
    ) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setCurrentQuarterVisible(entry.isIntersecting),
      { root: scrollAreaRef.current, threshold: 0 },
    );
    observer.observe(currentQuarterCellRef.current);
    return () => observer.disconnect();
  }, [hasCurrentQuarterColumn, minIndex, maxIndex]);

  const handleScrollToCurrentQuarter = () => {
    currentQuarterCellRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  const handleCardDragStart = (e, initiative) => {
    const span = initiative.estimate || 1;
    const rect = e.currentTarget.getBoundingClientRect();
    const grabFraction =
      rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0;
    const grabOffset = Math.min(
      span - 1,
      Math.max(0, Math.floor(grabFraction * span)),
    );
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(initiative.id));
    setDraggingId(initiative.id);
    setDragGrabOffset(grabOffset);
  };

  const handleCardDragEnd = () => {
    setDraggingId(null);
    setDragOverQuarter(null);
    setDragGrabOffset(0);
  };

  const resolveTargetQuarter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const columnWidth = rect.width / quarterCount;
    const columnIndex = Math.min(
      quarterCount - 1,
      Math.max(0, Math.floor((e.clientX - rect.left) / columnWidth)),
    );
    return Math.max(minIndex, minIndex + columnIndex - dragGrabOffset);
  };

  const handleGridDragOver = (e) => {
    if (draggingId != null) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverQuarter(resolveTargetQuarter(e));
    }
  };

  const handleGridDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverQuarter(null);
    }
  };

  const handleGridDrop = async (e) => {
    if (draggingId != null) {
      e.preventDefault();
      const draggedId = e.dataTransfer.getData("text/plain");
      const targetQuarter = resolveTargetQuarter(e);
      setDraggingId(null);
      setDragOverQuarter(null);
      setDragGrabOffset(0);
      const initiative = scheduled.find(
        (item) => String(item.id) === draggedId,
      );
      if (initiative || initiative.startIndex !== targetQuarter) {
        const targetPeriod = formatQuarterIndex(targetQuarter);
        setOptimisticMove({ id: initiative.id, targetPeriod });
        await onInitiativeChange?.(initiative.id, { targetPeriod });
        setOptimisticMove((current) =>
          current?.id === initiative.id &&
          current?.targetPeriod === targetPeriod
            ? null
            : current,
        );
      }
    }
  };

  const dividerStops = [];
  for (let i = 1; i < quarterCount; i += 1) {
    const pos = `(100% / ${quarterCount} * ${i})`;
    dividerStops.push(
      `transparent calc(${pos} - 1px)`,
      `${theme.vars.palette.divider} calc(${pos} - 1px)`,
      `${theme.vars.palette.divider} calc(${pos})`,
      `transparent calc(${pos})`,
    );
  }

  const styles = {
    root: {
      p: 3,
      display: "flex",
      justifyContent: "center",
    },
    surface: {
      position: "relative",
      bgcolor: "background.tableHeader",
      boxShadow: 3,
      borderRadius: 2,
      overflow: "hidden",
    },
    todayButton: {
      position: "absolute",
      top: 8,
      right: 8,
      zIndex: 1,
      bgcolor: "background.cardBackground",
      boxShadow: 2,
      "&:hover": { bgcolor: "background.cardBackground" },
    },
    grid: {
      display: "grid",
      gridTemplateColumns: `repeat(${quarterCount}, minmax(220px, 1fr))`,
      gridTemplateRows: `min-content repeat(${scheduled.length}, min-content) 8px`,
      rowGap: 2,
      minWidth: quarterCount * 220,
      backgroundImage: dividerStops.length
        ? `linear-gradient(to right, ${dividerStops.join(", ")})`
        : "none",
    },
    headerCell: (colIndex) => ({
      gridRow: 1,
      gridColumn: colIndex,
      px: 1.5,
      py: 1,
      bgcolor: "background.tableHeader",
      color: "background.tableHeaderText",
      fontWeight: 700,
      fontSize: "0.7rem",
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      borderBottom: 3,
      borderColor: "transparent",
    }),
    currentHeaderCell: {
      bgcolor: alpha(theme.palette.primary.main, 0.14),
      borderColor: "primary.main",
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: "1px",
        bgcolor: "background.tableHeader",
      },
    },
    dragOverHeaderCell: {
      bgcolor: alpha(theme.palette.primary.main, 0.28),
      borderColor: "primary.main",
    },
    quarterHighlight: (colIndex) => ({
      gridRow: `1 / span ${scheduled.length + 2}`,
      gridColumn: colIndex,
    }),
    quarterHighlightActive: {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
    },
    timelineCell: (row, startColumn, span) => ({
      gridRow: row + 2,
      gridColumn: `${startColumn} / span ${span}`,
      px: 1,
    }),
    scrollArea: { overflowX: "auto" },
    unscheduledNote: { color: "background.textMuted" },
    sortRow: {
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      mb: 1,
      flexWrap: "wrap",
    },
    sortLabel: {
      fontWeight: 700,
      fontSize: "0.7rem",
      color: "background.tableHeaderText",
      textTransform: "uppercase",
      letterSpacing: "0.07em",
    },
    sortChipsRow: { display: "flex", gap: 0.75, flexWrap: "wrap" },
    neutralChip: {
      borderColor: "background.tableHeaderText",
      color: "background.tableHeaderText",
    },
  };

  return scheduled.length === 0 ? (
    <Box sx={styles.root}>
      <Typography sx={styles.unscheduledNote}>
        No initiatives have a target period set.
      </Typography>
    </Box>
  ) : (
    <Stack sx={styles.root}>
      <Stack direction="row" alignItems="center" sx={styles.sortRow}>
        <Typography sx={styles.sortLabel}>Sort by</Typography>
        <Stack direction="row" sx={styles.sortChipsRow}>
          {SORT_OPTIONS.map((option) => {
            const isActive = (sortColumn ?? "default") === option.value;
            return (
              <Chip
                key={option.value}
                label={option.label}
                size="small"
                onClick={() => handleSortColumnChange(option.value)}
                color={isActive ? "primary" : undefined}
                variant={isActive ? "filled" : "outlined"}
                sx={isActive ? undefined : styles.neutralChip}
              />
            );
          })}
        </Stack>
        <IconButton
          size="small"
          onClick={handleToggleSortDir}
          disabled={!sortColumn}
          aria-label={sortDir === "asc" ? "Sort ascending" : "Sort descending"}
        >
          {sortDir === "asc" ? (
            <ArrowUpward fontSize="small" />
          ) : (
            <ArrowDownward fontSize="small" />
          )}
        </IconButton>
        {unscheduledCount > 0 && (
          <Typography variant="body2" sx={styles.unscheduledNote}>
            {unscheduledCount} initiative{unscheduledCount === 1 ? "" : "s"}{" "}
            without a target period {unscheduledCount === 1 ? "is" : "are"} not
            shown.
          </Typography>
        )}
      </Stack>
      <Box sx={styles.surface}>
        {hasCurrentQuarterColumn && !currentQuarterVisible && (
          <Tooltip title="Scroll to current quarter">
            <IconButton
              size="small"
              sx={styles.todayButton}
              onClick={handleScrollToCurrentQuarter}
              aria-label="Scroll to current quarter"
            >
              <Today fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={styles.scrollArea} ref={scrollAreaRef}>
          <Box
            sx={styles.grid}
            onDragOver={handleGridDragOver}
            onDragLeave={handleGridDragLeave}
            onDrop={handleGridDrop}
          >
            {draggingId != null &&
              quarters.map((quarterIndex) => (
                <Box
                  key={quarterIndex}
                  sx={[
                    styles.quarterHighlight(quarterIndex - minIndex + 1),
                    quarterIndex === dragOverQuarter
                      ? styles.quarterHighlightActive
                      : null,
                  ]}
                />
              ))}
            {quarters.map((quarterIndex) => (
              <Box
                key={quarterIndex}
                ref={
                  quarterIndex === currentQuarterIndex
                    ? currentQuarterCellRef
                    : undefined
                }
                sx={[
                  styles.headerCell(quarterIndex - minIndex + 1),
                  quarterIndex === currentQuarterIndex
                    ? styles.currentHeaderCell
                    : null,
                  quarterIndex === dragOverQuarter
                    ? styles.dragOverHeaderCell
                    : null,
                ]}
              >
                {formatQuarterIndex(quarterIndex)}
              </Box>
            ))}
            {scheduled.map((initiative, row) => (
              <Box
                key={initiative.id}
                sx={styles.timelineCell(
                  row,
                  initiative.startIndex - minIndex + 1,
                  initiative.estimate || 1,
                )}
              >
                <TimelineCard
                  initiative={initiative}
                  onClick={selection.select}
                  draggable={canDrag}
                  onDragStart={canDrag ? handleCardDragStart : undefined}
                  onDragEnd={canDrag ? handleCardDragEnd : undefined}
                  isDragging={draggingId === initiative.id}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <InitiativeDialog selection={selection} />
    </Stack>
  );
}
